import axios from "axios";
import { BASE_API_URL } from "../consts";

export type OvertimePayoutMode = "SALARY" | "TIME_OFF" | "OTHER";

export interface OvertimeAccrual {
  _id: string;
  userId: string;
  clientId: string;
  date: string;
  scheduledHours: number;
  actualHours: number;
  overtimeHours: number;
  payoutMode: OvertimePayoutMode;
  note?: string;
}

export interface OvertimeSummary {
  totalOvertimeHours: number;
  salaryHours: number;
  timeOffHours: number;
  otherHours: number;
  timeOffBalanceAllTime: number;
  defaultPayoutMode: OvertimePayoutMode;
}

export const getOvertimeAccruals = async (
  userId: string,
  start: string,
  end: string,
): Promise<OvertimeAccrual[]> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/users/${userId}/overtime-accruals`,
    { params: { start, end } },
  );
  return data;
};

export const getOvertimeSummary = async (
  userId: string,
  start: string,
  end: string,
): Promise<OvertimeSummary> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/users/${userId}/overtime-summary`,
    { params: { start, end } },
  );
  return data;
};

export const updateOvertimeAccrual = async (
  id: string,
  payload: { payoutMode?: OvertimePayoutMode; note?: string },
): Promise<OvertimeAccrual> => {
  const { data } = await axios.patch(
    `${BASE_API_URL}/overtime-accruals/${id}`,
    payload,
  );
  return data;
};

export const updateUserOvertimePayoutMode = async (
  userId: string,
  overtimePayoutMode: OvertimePayoutMode,
): Promise<{ _id: string; overtimePayoutMode: OvertimePayoutMode }> => {
  const { data } = await axios.patch(
    `${BASE_API_URL}/users/${userId}/overtime-payout-mode`,
    { overtimePayoutMode },
  );
  return data;
};

// Personal override of expected daily hours, checked before the employee's
// WorkingPattern/contract — history-based like a salary change: an entry
// takes effect from its month onward until a later month has its own entry.
export interface DefaultWorkHoursEntry {
  date: string; // first of the effective month, ISO
  hours: number;
}

export const getUserDefaultWorkHoursHistory = async (
  userId: string,
): Promise<DefaultWorkHoursEntry[]> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/users/${userId}/default-work-hours-history`,
  );
  return data;
};

// month: "YYYY-MM" — the month this value takes effect from (and onward,
// until a later month has its own entry).
export const upsertUserDefaultWorkHoursMonth = async (
  userId: string,
  month: string,
  hours: number,
): Promise<DefaultWorkHoursEntry[]> => {
  const { data } = await axios.put(
    `${BASE_API_URL}/users/${userId}/default-work-hours`,
    { month, hours },
  );
  return data;
};

export const deleteUserDefaultWorkHoursMonth = async (
  userId: string,
  month: string,
): Promise<DefaultWorkHoursEntry[]> => {
  const { data } = await axios.delete(
    `${BASE_API_URL}/users/${userId}/default-work-hours/${month}`,
  );
  return data;
};
